import {Action, Attributes, AttributesRow, Card, DataStatus, Separator} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetTreeFolderPathQuery} from '@dms/api/core';
import {useGetArticleQuery, useGetWarehouseQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getArticleDetailCardPrices} from '@dms/warehouse/shared';
import {ArticleDetailCardPrices} from '@dms/warehouse/shared-components';

import {composePath} from 'shared/web';

interface ArticleDetailCardProps {
  warehouseId: string;
  articleId: string;
}

export function ArticleDetailCard(props: ArticleDetailCardProps) {
  const articleQuery = useGetArticleQuery(
    {
      warehouseId: props.warehouseId as string,
      articleId: props.articleId as string,
    },
    {
      skip: isNilOrEmpty(props.warehouseId) || isNilOrEmpty(props.articleId),
    }
  );

  const warehouseQuery = useGetWarehouseQuery(
    {warehouseId: props.warehouseId as string},
    {skip: isNilOrEmpty(articleQuery.data?.warehouseId), refetchOnMountOrArgChange: true}
  );

  const categoryQuery = useGetTreeFolderPathQuery(
    {leafId: articleQuery.data?.treeFolder?.leafId as string},
    {skip: isNilOrEmpty(articleQuery.data?.treeFolder?.leafId), refetchOnMountOrArgChange: true}
  );

  const isLoading = articleQuery.isFetching || warehouseQuery.isLoading || categoryQuery.isLoading;

  const doesArticleExist = isNotNil(articleQuery.data?.id);

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.refresh'),
      leftIcon: 'navigation/refresh',
      onClick: () => articleQuery.refetch(),
    },
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('entity.warehouse.actions.viewArticle'),
      leftIcon: 'action/open_in_new',
      onClick: () =>
        window.open(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {warehouseId: props.warehouseId, id: props.articleId},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        ),
    },
  ];

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: articleQuery.data?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      value: articleQuery.data?.manufacturerNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: articleQuery.data?.manufacturerName,
    },
    {
      label: i18n.t('entity.warehouse.labels.storageLocation'),
      value: articleQuery.data?.storageLocation,
    },
    {
      label: i18n.t('entity.warehouse.labels.category'),
      value: categoryQuery.data?.treeFolder?.pathName,
    },
  ];

  const prices = getArticleDetailCardPrices(articleQuery.data);

  const currency = warehouseQuery.data?.currency;

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.articleDetail')}
      actions={doesArticleExist ? actions : undefined}
    >
      <VStack>
        <DataStatus isLoading={isLoading} minHeight={65}>
          <Attributes rows={rows} size="quarter" />
          <Separator spacing={3} />
          <ArticleDetailCardPrices prices={prices} currency={currency} />
        </DataStatus>
      </VStack>
    </Card>
  );
}
